/**
 * Perfis Service - Epic 27: Controle de Acesso
 *
 * Serviço para gestão de Perfis de Acesso
 * Endpoints: GET /api/perfis/, GET /api/perfis/{id}/, GET /api/perfis/{id}/permissoes/
 */
import apiClient from './apiClient';

// ============================================================================
// TIPOS E INTERFACES
// ============================================================================

export interface Perfil {
  id: number;
  codigo: string;
  nome: string;
  nivel_acesso: number;
  ativo: boolean;
  total_permissoes: number;
}

export interface PerfilDetalhado extends Perfil {
  descricao: string;
  created_at: string;
  updated_at: string;
}

export interface Permissao {
  pode_visualizar: boolean;
  pode_criar: boolean;
  pode_editar: boolean;
  pode_excluir: boolean;
  pode_exportar: boolean;
}

export interface TelaPermissao {
  tela_id: number;
  tela_codigo: string;
  tela_nome: string;
  modulo: string;
  ordem_menu: number;
  permissoes: Permissao;
}

export interface PerfilComPermissoes {
  perfil: PerfilDetalhado;
  permissoes: TelaPermissao[];
}

// ============================================================================
// SERVIÇO
// ============================================================================

class PerfisService {
  /**
   * Lista todos os perfis de acesso
   */
  async listarPerfis(): Promise<Perfil[]> {
    const response = await apiClient.get<Perfil[]>('/perfis/');
    return response.data;
  }

  /**
   * Busca detalhes de um perfil específico
   */
  async buscarPerfil(perfilId: number): Promise<PerfilDetalhado> {
    const response = await apiClient.get<PerfilDetalhado>(`/perfis/${perfilId}/`);
    return response.data;
  }

  /**
   * Busca todas as permissões de um perfil (matriz completa)
   */
  async buscarPermissoesPerfil(perfilId: number): Promise<PerfilComPermissoes> {
    const response = await apiClient.get<PerfilComPermissoes>(`/perfis/${perfilId}/permissoes/`);
    return response.data;
  }

  /**
   * Verifica se um perfil tem permissão específica em uma tela
   */
  async perfilTemPermissao(
    perfilId: number,
    telaId: number,
    tipoPermissao: keyof Permissao
  ): Promise<boolean> {
    const perfilComPermissoes = await this.buscarPermissoesPerfil(perfilId);
    const telaPermissao = perfilComPermissoes.permissoes.find(p => p.tela_id === telaId);
    return telaPermissao ? telaPermissao.permissoes[tipoPermissao] : false;
  }

  /**
   * Lista permissões de um perfil filtradas por módulo
   */
  async buscarPermissoesPorModulo(perfilId: number, modulo: string): Promise<TelaPermissao[]> {
    const perfilComPermissoes = await this.buscarPermissoesPerfil(perfilId);
    return perfilComPermissoes.permissoes.filter(p => p.modulo === modulo);
  }

  /**
   * Conta quantas telas um perfil tem acesso (pode_visualizar = true)
   */
  async contarAcessosPerfil(perfilId: number): Promise<number> {
    const perfilComPermissoes = await this.buscarPermissoesPerfil(perfilId);
    return perfilComPermissoes.permissoes.filter(p => p.permissoes.pode_visualizar).length;
  }
}

export default new PerfisService();
